import { makeStyles } from '@material-ui/core/styles';

// DQ AI chatbot preview page — layout mirrors CurationPromptApplicationStyles
// (login-free chat page precedent), plus a collected-data status panel.
const useStyles = makeStyles((theme) => ({
    pageWrapper: {
        background: 'var(--brand-bg, #fff)',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 24px',
        height: 64,
        borderBottom: '1px solid var(--brand-border, #F0F0F0)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        flexShrink: 0,
        background: 'var(--brand-bg, #fff)',
        position: 'relative',
        gap: 12,
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: 'linear-gradient(90deg, #FF6900 0%, #FF8C33 100%)',
        },
    },
    headerTitleWrap: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    sessionCode: {
        fontFamily: 'monospace',
        fontSize: 12,
        color: 'var(--brand-text-muted, #5f6368)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
    },
    mainRow: {
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    chatColumn: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    chatPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    chatScroll: {
        flex: 1,
        overflowY: 'auto',
        padding: '18px 20px',
        background: 'var(--brand-bg, #FBFAF8)',
    },
    // Brand-orange user bubbles (DQProV2 palette) + white card bubbles with a small
    // "DQ" avatar for the assistant.
    botBubbleWrap: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 8,
        marginBottom: 14,
    },
    userBubbleWrap: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 14,
    },
    botAvatar: {
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #FF6900 0%, #FF8C33 100%)',
        color: '#fff',
        fontSize: 11,
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginTop: 2,
        boxShadow: '0 1px 3px rgba(255,105,0,0.35)',
    },
    botBubble: {
        background: 'var(--brand-surface, #fff)',
        color: 'var(--brand-text, #1a1a1a)',
        padding: '12px 14px',
        border: '1px solid var(--brand-border, #EFE9E4)',
        borderRadius: '4px 14px 14px 14px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        maxWidth: '92%',
        fontSize: 14,
        lineHeight: 1.6,
        wordBreak: 'break-word',
        '& > span > div:last-child': {
            marginBottom: '0 !important',
        },
    },
    userBubble: {
        background: 'linear-gradient(135deg, #FF6900 0%, #FF8C33 100%)',
        color: '#fff',
        fontWeight: 600,
        padding: '10px 14px',
        borderRadius: '14px 14px 4px 14px',
        boxShadow: '0 1px 3px rgba(255,105,0,0.25)',
        maxWidth: '85%',
        fontSize: 14,
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },

    // ── Typing indicator (three blinking dots in a bot bubble) ──
    typingBubble: {
        background: 'var(--brand-surface, #fff)',
        border: '1px solid var(--brand-border, #EFE9E4)',
        borderRadius: '4px 14px 14px 14px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },
    typingDot: {
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: '#FFB380',
        animation: '$dqaiBlink 1.2s infinite ease-in-out',
    },
    '@keyframes dqaiBlink': {
        '0%, 80%, 100%': { opacity: 0.25, transform: 'scale(0.85)' },
        '40%': { opacity: 1, transform: 'scale(1)' },
    },

    // ── Blank state before a session is started ──
    emptyState: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        color: 'var(--brand-text-muted, #8a8a8a)',
        background: 'var(--brand-bg, #FBFAF8)',
        textAlign: 'center',
        padding: 24,
    },
    emptyBadge: {
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #FF6900 0%, #FF8C33 100%)',
        color: '#fff',
        fontSize: 20,
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 14px rgba(255,105,0,0.3)',
    },

    // ── Collected-data status panel (the notebook's 📋 Collected data panel) ──
    statusColumn: {
        width: 320,
        flexShrink: 0,
        overflowY: 'auto',
        borderLeft: '1px solid var(--brand-border, #e8eaed)',
        padding: 14,
        background: 'var(--brand-surface, #fafafa)',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            maxHeight: 220,
            borderLeft: 'none',
            borderTop: '1px solid var(--brand-border, #e8eaed)',
        },
    },
    statusTable: {
        width: '100%',
        fontSize: 13,
        marginTop: 6,
        borderCollapse: 'collapse',
        '& td': {
            padding: '4px 6px',
            verticalAlign: 'top',
        },
    },
    statusLabel: {
        color: 'var(--brand-text-muted, #5f6368)',
        whiteSpace: 'nowrap',
    },
    statusValue: {
        fontWeight: 600,
        color: 'var(--brand-text, #202124)',
        wordBreak: 'break-word',
    },
    statusEmpty: {
        color: '#bbb',
    },
    statusPending: {
        color: '#b06000',
    },
    statusBadge: {
        marginTop: 10,
        padding: '6px 8px',
        textAlign: 'center',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 600,
    },
    badgeCollecting: {
        background: '#fef7e0',
        color: '#b06000',
    },
    badgeDone: {
        background: '#e6f4ea',
        color: '#137333',
    },
}));

export default useStyles;
